import {
   FormControl,
   FormHelperText,
   Grid,
   InputAdornment,
   InputLabel,
   MenuItem,
   Select,
   TextField,
   Typography,
} from '@mui/material';
import { useFormikContext } from 'formik';
import {
   CALCULATED,
   DERIVED,
   DETAIL_TYPES,
   applyDetailChange,
   isBlank,
   resolveDetail,
   round2,
   timeUnitLabel,
} from '../../Utils/quotationDetails';

/** Short explanation shown under the section title. */
export function QuotationDetailsHelp() {
   return (
      <Typography variant="body2" color="text.secondary" component="div" sx={{ mb: 1 }}>
         <div>
            <strong>Time + Charge Per Hour:</strong> Cost = time × hours in the unit × charge per
            hour.
         </div>
         <div>
            <strong>Time + Cost (no charge):</strong> Charge Per Hour is calculated from the cost.
         </div>
         <div>
            <strong>Fixed / One Time:</strong> leave time and charge empty and enter the Cost. One
            Time time is in hours.
         </div>
      </Typography>
   );
}

const errorTextSx = { color: '#d32f2f', margin: '3px 14px' };

/**
 * The five inputs of one "Development Time & Cost" row, shared by the quotation Add and Edit forms.
 * Renders Grid items, so it must sit inside a <Grid container>.
 */
function QuotationDetailFields({ index, symbol, hoursPerUnit }) {
   const { values, touched, errors, setFieldValue, setFieldTouched } = useFormikContext();
   const row = values.quotationDetails[index];
   const resolved = resolveDetail(row, hoursPerUnit);
   const calculated = resolved.mode === CALCULATED;
   const rateAutoFilled =
      isBlank(row.chargePerHour) && resolved.mode === DERIVED && resolved.chargePerHour != null;

   const name = (field) => `quotationDetails.${index}.${field}`;
   const error = (field) =>
      touched.quotationDetails?.[index]?.[field] && errors.quotationDetails?.[index]?.[field];
   const change = (field) => (e) =>
      applyDetailChange(setFieldValue, index, row, field, e.target.value, hoursPerUnit);
   const blur = (field) => () => setFieldTouched(name(field), true);

   return (
      <>
         <Grid item xs={12} md={4}>
            <TextField
               fullWidth
               label="Description*"
               name={name('description')}
               variant="outlined"
               placeholder="Enter Project delivery here..."
               value={row.description}
               onChange={(e) => setFieldValue(name('description'), e.target.value)}
               onBlur={blur('description')}
               error={Boolean(error('description'))}
               helperText={error('description')}
            />
         </Grid>

         <Grid item xs={12} md={2}>
            <FormControl fullWidth error={Boolean(error('type'))}>
               <InputLabel id={`type-label-${index}`}>Type</InputLabel>
               <Select
                  labelId={`type-label-${index}`}
                  label="Type"
                  name={name('type')}
                  value={row.type}
                  onChange={(e) => setFieldValue(name('type'), e.target.value)}
                  onBlur={blur('type')}
               >
                  <MenuItem value="">
                     <em>None</em>
                  </MenuItem>
                  {DETAIL_TYPES.map((type) => (
                     <MenuItem key={type} value={type}>
                        {type}
                     </MenuItem>
                  ))}
               </Select>
               {error('type') && <FormHelperText sx={errorTextSx}>{error('type')}</FormHelperText>}
            </FormControl>
         </Grid>

         <Grid item xs={12} md={2}>
            <TextField
               fullWidth
               type="number"
               variant="outlined"
               label={`Development Time (in ${timeUnitLabel(row.type)})`}
               placeholder="Optional"
               name={name('developmentTime')}
               value={row.developmentTime}
               onChange={change('developmentTime')}
               onBlur={blur('developmentTime')}
               inputProps={{ min: 0 }}
               error={Boolean(error('developmentTime'))}
               helperText={error('developmentTime')}
            />
         </Grid>

         <Grid item xs={12} md={2}>
            <TextField
               fullWidth
               type="number"
               variant="outlined"
               label="Charge Per Hour"
               placeholder="Optional"
               name={name('chargePerHour')}
               value={rateAutoFilled ? resolved.chargePerHour : row.chargePerHour}
               onChange={change('chargePerHour')}
               onBlur={blur('chargePerHour')}
               inputProps={{ min: 0, step: '0.01' }}
               error={Boolean(error('chargePerHour'))}
               helperText={
                  error('chargePerHour') || (rateAutoFilled ? 'Auto-calculated' : undefined)
               }
            />
         </Grid>

         <Grid item xs={12} md={2}>
            <TextField
               fullWidth
               type="number"
               variant="outlined"
               label="Cost"
               name={name('total')}
               value={calculated ? round2(resolved.total) : row.total}
               onChange={change('total')}
               onBlur={blur('total')}
               inputProps={{ min: 0, step: '0.01' }}
               InputProps={{
                  startAdornment: <InputAdornment position="start">{symbol}</InputAdornment>,
               }}
               error={Boolean(error('total'))}
               helperText={error('total') || (resolved.mode === 'fixed' ? 'Fixed cost' : undefined)}
            />
         </Grid>

         <Grid item xs={12} md={6}>
            <TextField
               fullWidth
               variant="outlined"
               label="Approx Development Time"
               placeholder="Optional, e.g. 2-3 weeks. If given, Type, Time and Charge Per Hour are ignored"
               name={name('approxDevelopmentTime')}
               value={row.approxDevelopmentTime ?? ''}
               onChange={change('approxDevelopmentTime')}
               onBlur={blur('approxDevelopmentTime')}
               inputProps={{ maxLength: 100 }}
            />
         </Grid>
      </>
   );
}

export default QuotationDetailFields;
